import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { getDeviceId } from '../device';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/Button';
import Icon from '../components/Icon';

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, enterGuest } = useAuth();
  const { colors } = useTheme();

  const browseAsGuest = () => { enterGuest(); navigate('/browse'); };
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e?: FormEvent) => {
    e?.preventDefault();
    setError('');
    if (!username.trim() || !password.trim()) {
      setError(t('auth.login.errors.missingFields'));
      return;
    }
    setLoading(true);
    try {
      const deviceId = await getDeviceId().catch(() => null);
      // withCredentials: true so the server's Set-Cookie response (httpOnly
      // refresh-token cookie, scoped to /api/v1/auth/refresh) actually lands
      // in the browser's cookie jar — H1 phase 2.
      const res = await axios.post(
        `${API_BASE}/auth/login`,
        { username: username.trim(), password },
        {
          withCredentials: true,
          headers: deviceId ? { 'X-Device-Id': deviceId } : undefined,
        },
      );
      const { access_token, user } = res.data;
      await login(access_token, user.id);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { message?: string } } };
      const serverMsg = e.response?.data?.message ?? '';
      if (e.response?.status === 401) {
        if (typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('banned') || serverMsg.toLowerCase().includes('suspended'))) {
          setError(serverMsg);
        } else {
          setError(t('auth.login.errors.invalidCredentials'));
        }
      } else if (e.response?.status === 403) {
        setError(serverMsg || t('auth.login.errors.notAllowed'));
      } else {
        setError(t('auth.login.errors.connection'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: colors.bg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '24px max(24px, env(safe-area-inset-left))',
      }}
    >
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/icons/icon-512.png" alt="" style={{ width: 84, height: 84, objectFit: 'contain', marginBottom: 10 }} />
          <div style={{ color: colors.text, fontSize: 30, fontWeight: 900, letterSpacing: 2 }}>AGA</div>
          <div style={{ color: colors.textSub, fontSize: 13, fontWeight: 500, marginTop: 2 }}>{t('auth.login.appNameSub')}</div>
          <div style={{ color: colors.textMuted, fontSize: 13, marginTop: 4 }}>{t('auth.login.tagline')}</div>
        </div>

        <form
          onSubmit={handleLogin}
          style={{
            backgroundColor: colors.card,
            borderRadius: 16,
            padding: 22,
            border: `1px solid ${colors.cardBorder}`,
          }}
        >
          <div style={{ fontSize: 20, fontWeight: 700, color: colors.text, marginBottom: 16 }}>{t('auth.login.title')}</div>

          {error ? (
            <div
              style={{
                backgroundColor: colors.dangerSoft,
                borderRadius: 8,
                padding: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 14,
                border: `1px solid ${colors.danger}`,
              }}
            >
              <Icon name="exclamation-circle" size={14} color={colors.dangerText} />
              <div style={{ color: colors.dangerText, fontSize: 13, flex: 1 }}>{error}</div>
            </div>
          ) : null}

          <Field label={t('auth.login.username')}>
            <Icon name="user" size={14} color={colors.textSub} style={{ paddingLeft: 14, paddingRight: 10 }} />
            <input
              style={inputStyle(colors.inputText)}
              placeholder={t('auth.login.usernamePlaceholder')}
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
            />
          </Field>

          <Field label={t('auth.login.password')}>
            <Icon name="lock" size={14} color={colors.textSub} style={{ paddingLeft: 14, paddingRight: 10 }} />
            <input
              style={{ ...inputStyle(colors.inputText), flex: 1 }}
              placeholder={t('auth.login.passwordPlaceholder')}
              type={showPass ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoCapitalize="none"
              autoComplete="current-password"
            />
            <button
              type="button"
              aria-label={showPass ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
              onClick={() => setShowPass(v => !v)}
              style={{
                width: 50,
                minHeight: 50,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name={showPass ? 'eye-slash' : 'eye'} size={14} color={colors.textSub} />
            </button>
          </Field>

          <Button
            label={t('auth.login.submit')}
            icon="sign-in-alt"
            loading={loading}
            type="submit"
            style={{ marginTop: 22 }}
          />

          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              marginTop: 16,
              padding: '8px 0',
              minHeight: 44,
              width: '100%',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: colors.textMuted,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            <Icon name="question-circle" size={13} color={colors.textMuted} />
            {t('auth.login.forgotPassword')}
          </button>
        </form>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 28 }}>
          <div style={{ color: colors.textMuted, fontSize: 13 }}>{t('auth.login.noAccount')}</div>
          <button
            onClick={() => navigate('/register')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              backgroundColor: colors.card,
              padding: '12px 20px',
              borderRadius: 10,
              border: `1px solid ${colors.cardBorder}`,
              minHeight: 50,
              cursor: 'pointer',
              color: colors.accent,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <Icon name="user-plus" size={13} color={colors.accent} />
            {t('auth.login.createAccount')}
          </button>

          <button
            onClick={browseAsGuest}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: colors.textMuted, fontSize: 13, fontWeight: 600, minHeight: 44,
            }}
          >
            <Icon name="eye" size={13} color={colors.textMuted} />
            {t('auth.login.browseAsGuest')}
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: colors.textMuted, lineHeight: 1.6 }}>
          {t('auth.login.supportPrompt')}{' '}
          <a href={`mailto:${t('auth.login.supportEmail')}`} style={{ color: colors.accent, fontWeight: 700, textDecoration: 'none' }}>
            {t('auth.login.supportEmail')}
          </a>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  const { colors } = useTheme();
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: colors.textMuted,
          marginBottom: 6,
          marginTop: 14,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: colors.input,
          borderRadius: 8,
          border: `1px solid ${colors.inputBorder}`,
          minHeight: 50,
        }}
      >
        {children}
      </div>
    </div>
  );
}

const inputStyle = (color: string): React.CSSProperties => ({
  flex: 1,
  padding: '13px 14px 13px 0',
  fontSize: 15,
  color,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  width: '100%',
});
